import { HubConnectionState } from '@microsoft/signalr';
import { useContext, useEffect, useRef, useState } from 'react';
import AuthContext from '../AuthContext';
import { useNotifications } from '../NotificationContext';
import type { PresenceLocation, PresenceResourceType, PresenceViewer } from './PresenceContract';
export type { PresenceLocation, PresenceResourceType, PresenceViewer } from './PresenceContract';

const HEARTBEAT_MS = 15000;
const EXPIRY_MS = 45000;

export function usePresence(resourceType: PresenceResourceType, resourceId: string | undefined, scope: string, selectedLocation: PresenceLocation | null, observeOnly = false) {
	const { connection, connectionState } = useNotifications();
	const { account } = useContext(AuthContext);
	const [roster, setRoster] = useState<{ connection: typeof connection; resourceType: PresenceResourceType; resourceId: string; scope: string; viewers: PresenceViewer[] }>();
	const location = useRef(selectedLocation);
	location.current = selectedLocation;
	const publishRef = useRef<() => void>(() => undefined);
	// Serialize lifecycle calls so an old scope's cleanup cannot leave a newer scope.
	const operations = useRef<Promise<unknown>>(Promise.resolve());
	const ownUserId = String(account?.idTokenClaims?.oid ?? account?.idTokenClaims?.sub ?? account?.localAccountId ?? '').toLowerCase();

	useEffect(() => publishRef.current(), [selectedLocation?.itemType, selectedLocation?.itemId, selectedLocation?.relatedItemType, selectedLocation?.relatedItemId]);

	useEffect(() => {
		setRoster(undefined);
		if (!connection || connectionState !== HubConnectionState.Connected || !resourceId) {
			return;
		}
		let disposed = false;
		const peers = new Map<string, { viewer: PresenceViewer; seen: number }>();
		const refresh = () => {
			if (disposed) return;
			const now = Date.now();
			peers.forEach((peer, id) => {
				if (now - peer.seen >= EXPIRY_MS) {
					peers.delete(id);
				}
			});
			setRoster({ connection, resourceType, resourceId, scope, viewers: Array.from(peers.values(), (peer) => peer.viewer) });
		};
		const enqueue = (action: () => Promise<unknown>) => {
			operations.current = operations.current.catch(() => undefined).then(action).catch(() => undefined);
		};
		const publish = () => enqueue(async () => {
			if (disposed || connection.state !== HubConnectionState.Connected) {
				return;
			}
			const current = location.current;
			if (observeOnly) {
				await connection.invoke('WatchPresence', resourceType, resourceId, scope);
			} else {
				await connection.invoke('UpdatePresence', resourceType, resourceId, scope, current?.itemType ?? null, current?.itemId ?? null, current?.relatedItemType ?? null, current?.relatedItemId ?? null);
			}
		});
		const receive = (viewer: PresenceViewer) => {
			if (disposed || viewer.resourceType !== resourceType || viewer.resourceId.toLowerCase() !== resourceId.toLowerCase() || viewer.scope !== scope || viewer.connectionId === connection.connectionId) {
				return;
			}
			if (viewer.left) {
				peers.delete(viewer.connectionId);
			} else if (viewer.userId.toLowerCase() !== ownUserId) {
				peers.set(viewer.connectionId, { viewer, seen: Date.now() });
			}
			refresh();
		};
		const requested = (requestedType: string, requestedId: string, requestedScope: string) => {
			if (!observeOnly && !disposed && requestedType === resourceType && requestedId.toLowerCase() === resourceId.toLowerCase() && requestedScope === scope) {
				publish();
			}
		};
		connection.on('PresenceChanged', receive);
		connection.on('PresenceRequested', requested);
		publishRef.current = publish;
		publish();
		const heartbeat = window.setInterval(() => { publish(); refresh(); }, HEARTBEAT_MS);
		return () => {
			disposed = true;
			publishRef.current = () => undefined;
			window.clearInterval(heartbeat);
			connection.off('PresenceChanged', receive);
			connection.off('PresenceRequested', requested);
			enqueue(async () => {
				if (connection.state === HubConnectionState.Connected) {
					await connection.invoke(observeOnly ? 'UnwatchPresence' : 'LeavePresence', resourceType, resourceId, scope);
				}
			});
		};
	}, [connection, connectionState, resourceType, resourceId, scope, ownUserId, observeOnly]);

	// Never show a previous resource/connection's roster, even before effect cleanup runs.
	return roster && roster.connection === connection && connectionState === HubConnectionState.Connected
		&& roster.resourceType === resourceType && roster.resourceId.toLowerCase() === resourceId?.toLowerCase() && roster.scope === scope
		? roster.viewers : [];
}
